import React, { useState, useEffect } from 'react';
import { TextField, Button, Typography, Grid, Box } from '@mui/material';
import { PrivateUserInfoRequestModel, UserInfosModel } from '../../models/user/UserInfosModel';
import { useTranslationContext } from '../../hooks/translations/translationsProvider';
import TextFieldCustom from '../TextField/TextFieldCustom';
import SearchNations from './searchNation';
import DatePickerCustom from '../DatePicker/DatePickerCustom';
import dayjs, { Dayjs } from 'dayjs';
import lastDate18YearsAgo from '../../utilities/lastDate18YearsAgo';
import { BLUE_DARK, GRAY_LIGHT, GRAY_MAIN, GREEN_LOGO, GREEN_MAIN, WHITE } from '../../theme/colors';
import { getAssociationStatusLabel } from '../../helpers/approverFromAssociationHelper';
import { putMePrivate } from '../../hooks/actions/users';
import AlertSaveModal from './AlertSaveModal';
import { useWaiter } from '../../hooks/waiter/WaiterProvider';
import { Snackbar, Alert } from '@mui/material';
import { ESex } from '../../hooks/auth/models';
import SexSelect from './DropDownSex';
import { useUserContext } from '../../hooks/auth/UserProvider';

interface PrivateUserProps {
  user: UserInfosModel; // L'interfaccia IUser definita precedentemente.
}

const PrivateUser: React.FC<PrivateUserProps> = ({ user }) => {
  const { t } = useTranslationContext();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<PrivateUserInfoRequestModel>({
    userId: user.userId || '', // ID dell'utente privato
    isPrivate: user.isPrivate,
    conventioned: user.conventioned,
    email: user.email,
    enabledUser: user.enabledUser === true,
    username: user.username,
    userType: user.userType,
    refCode: user.refCode || '',
    association: user.association || '',
    lastDateAssociationAction: user.lastDateAssociationAction || undefined,
    removedFromAssociation: user.removedFromAssociation || undefined,
    approvedFromAssociation: Number(user.approvedFromAssociation) || 0,

    // Dati specifici dell'utente privato (PrivateInfos)
    userInfoId: user.privateInfo?.id || '',
    nome: user.privateInfo?.nome || '',
    cognome: user.privateInfo?.cognome || '',
    sex: user.privateInfo?.sex || ESex.Male,
    born: user.privateInfo?.born || undefined,
    address: user.privateInfo?.address || '',
    comuneDiNascita: user.privateInfo?.comuneDiNascita || '',
    statoDiNascita: user.privateInfo?.statoDiNascita || '',
    city: user.privateInfo?.city || '',
    cap: user.privateInfo?.cap || 0,
    provincia: user.privateInfo?.provincia || '',
    nation: user.privateInfo?.nation || '',
    codiceFiscale: user.privateInfo?.codiceFiscale || '',
    professione: user.privateInfo?.professione || '',
    telephone: user.telephone || '',
  });
  const [showPopup, setShowPopup] = useState(false);
  const {setWaiterView} = useWaiter();
  const [initialFormData, setInitialFormData] = useState({ ...formData }); // Salva i dati iniziali

  const [modalOpen, setModalOpen] = useState(false);
  const bornDate = initialFormData.born ? dayjs(initialFormData.born) : dayjs("01-01-1900")
  const [selectedDate, setSelectedDate] = useState<Dayjs | undefined>(bornDate); 
  const [bornError, setBornError] = useState(false);
  const [bornErrorMessage, setBornErrorMessage] = useState('');
  const [maxData] = useState<Dayjs | undefined>(dayjs(lastDate18YearsAgo()));

  const [mainError, setMainError] = useState<boolean>(false);
  const [mainErrorMex, setMainErrorMex] = useState<string>("");

  const {isForbidden} = useUserContext();

  const handleChange = (value: string, key: string) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value, // Directly update with the string value
    }));
  };

  const handleEdit = () => {
    setMainError(false); // cancello eventuali errori
    setMainErrorMex("");

    setIsEditing(true);
  };

  const handleSave = () => {
    
    setMainError(false); // cancello eventuali errori
    setMainErrorMex("");

    handleCloseModal(); // chiudo il modal

    if(JSON.stringify(formData) === JSON.stringify(initialFormData)){ // verifico che i dati non siano gli stessi
      setIsEditing(false);

      setShowPopup(true); // apertura e chiusura del popup
      setTimeout(() => {
        setShowPopup(false);
      }, 10000);

      return;
    }
    setWaiterView(true); // avvio il waiter

    putMePrivate(formData).then(resp => {
      setFormData(resp);
      setInitialFormData(resp);
      setWaiterView(false); // termina il waiter
      
      setShowPopup(true); // apertura e chiusura del popup
      setTimeout(() => {
        setShowPopup(false);
      }, 10000);

    }).catch(error => {
      // settare il main error
      setMainError(true);
      const errorMex =  error?.response?.data?.title || 
                        error?.response?.data?.message || 
                        error?.response?.data || 
                        error?.response || 
                        t("Errore durante la modifica dei dati")
      setMainErrorMex(errorMex);
    });
    setWaiterView(false); // termina il waiter
    setIsEditing(false); // disabilito la modifica
  };

  const handleCancel = () => {
    setMainError(false); // cancello eventuali errori
    setMainErrorMex("");

    setFormData(initialFormData); // Reset dei dati al valore iniziale
    setIsEditing(false);
  };

  // Conditionally render the component output
  if (!user.userId) {
    return <div>{t("Utente privato non disponibile.")}</div>;
  }

  const handleChangeDate = (date: Dayjs | null) => {
    setBornErrorMessage('');

    if (date !== null && date.isValid()) {
      setSelectedDate(date);
      setBornError(false);

      if (maxData !== undefined && date.isAfter(maxData)) {
        setBornErrorMessage(t('Devi avere più di 18 anni per iscriverti'));
        setBornError(true);
      }

      // 🔧 Normalizza la data rimuovendo l'ora
      const normalized = date.startOf('day').toDate();

      setFormData((prev) => ({
        ...prev,
        born: normalized, // ora sarà "1977-02-01T00:00:00.000Z"
      }));
    }

    if (date !== null && !date.isValid()) {
      setBornErrorMessage(t('Data non valida'));
      setBornError(true);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  return (
    <Box
      width='100%'
    >
      <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{
              mb: 8,
              mt:0,
              fontWeight: "bold",
              color: GREEN_MAIN
          }}
      >
          {t("Informazioni Utente")}
      </Typography>
      
      <form>
        <Box justifyItems='center'>
          <Grid container xs={12} md={8} spacing={3} paddingBottom='100px'>
            <Grid item xs={12} sm={6}>
              <TextFieldCustom
                label={t("Nome")}
                regex={new RegExp("^[\\p{L} \\-']+$", "u")}
                value={formData.nome || ''}
                errorText={t("Richieste lettere e spazi")}
                onChange={(value) => handleChange(value, 'nome')}
                disable={(!initialFormData.nome || initialFormData.nome === '') && isEditing ? false : true}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextFieldCustom
                label={t("Cognome")}
                regex={new RegExp("^[\\p{L} \\-']+$", "u")}
                value={formData.cognome || ''}
                errorText={t("Richieste lettere e spazi")}
                onChange={(value) => handleChange(value, 'cognome')}
                disable={(!initialFormData.cognome || initialFormData.cognome === '') && isEditing ? false : true}
              />
            </Grid>
            <Grid item xs={4} sm={2} >
              <SexSelect 
                value={formData.sex || ESex.Male}
                onChange={(value) => handleChange(value, 'sex')}
                disable={(!initialFormData.sex) && isEditing ? false : true} />
            </Grid>
            <Grid item xs={8} sm={4} >
              <DatePickerCustom
                label={t("Data di Nascita")}
                selectedDate={selectedDate}
                handleChangeDate={handleChangeDate}
                maxData={maxData}
                error={bornError}
                errorMessage={bornErrorMessage}
                disable={(!initialFormData.born) && isEditing ? false : true} //
                stackProps={{
                  sx: {
                    marginTop:0,
                    marginBottom:0,
                    '& .MuiOutlinedInput-notchedOutline': {
                        // borderColor: GREEN_LOGO,
                        color: GREEN_MAIN,
                    },
                    '& .MuiInputLabel-root': {
                        top: -4,
                        fontSize: '1rem',
                        //color: GREEN_MAIN,
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                        color: GREEN_MAIN,
                    },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: GREEN_LOGO,
                        color: GREEN_MAIN,
                    },
                    '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'grey !important',
                    },
                    '& .MuiOutlinedInput-input': {
                        padding: '12px 12px',
                        lineHeight: 1.4,
                        color: "grey",
                        fontSize: '1rem',
                    },
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextFieldCustom
                label={t("Codice Fiscale")}
                regex={/^[A-Za-z0-9]*$/}
                value={formData.codiceFiscale || '' }
                onChange={(value) => handleChange(value, 'codiceFiscale')}
                required={true}
                errorText={t("Richiesti lettere e numeri")}
                disable={!isEditing}
                props={{
                  sx: {
                    '& .MuiOutlinedInput-notchedOutline': {
                        // borderColor: GREEN_LOGO,
                        color: GREEN_MAIN,
                    },
                    '& .MuiInputLabel-root': {
                        top: -4,
                        fontSize: '1rem',
                        //color: GREEN_MAIN,
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                        color: GREEN_MAIN,
                    },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: GREEN_LOGO,
                        color: GREEN_MAIN,
                    },
                    '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'grey !important',
                    },
                    '& .MuiOutlinedInput-input': {
                        padding: '12px 12px',
                        lineHeight: 1.4,
                        color: "grey",
                        fontSize: '1rem',
                        textTransform: 'uppercase'
                    },
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={7}>
              <TextFieldCustom
                label={t("Comune di nascita")}
                regex={new RegExp("^[\\p{L}\\p{M} \\-']+$", "u")}
                value={formData.comuneDiNascita || ''}
                onChange={(value) => handleChange(value, 'comuneDiNascita')}
                required={true}
                errorText={t("Richieste lettere e spazi")}
                disable={(!initialFormData.comuneDiNascita || initialFormData.comuneDiNascita === '')  && isEditing ? false : true}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <SearchNations 
                label={t("Stato di nascita")}
                nazione={formData.statoDiNascita || ''} 
                setNazione={(value) => handleChange(value, 'statoDiNascita')} 
                disable={(!initialFormData.statoDiNascita || initialFormData.statoDiNascita === '') && isEditing ? false : true}
              />
            </Grid>
          </Grid>

          <Grid container xs={12} md={8} spacing={3} paddingBottom='30px'>
            <Grid item xs={12} sm={6}>
              <TextFieldCustom
                label={t("Indirizzo")}
                regex={new RegExp("^[\\p{L}\\p{N}\\p{M}\\p{P}\\p{Z}\\p{S} ,\\.\\-\\/]+$", "u")}
                value={formData.address || ''}
                onChange={(value) => handleChange(value, 'address')}
                required={true}
                errorText={t("Richieste solo lettere, spazi, numeri, virgola e meno e /")}
                disable={!isEditing}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextFieldCustom
                label={t("Città")}
                regex={ new RegExp("^[\\p{L}\\p{M} \\-']+$", "u")}
                value={formData.city || ''}
                onChange={(value) => handleChange(value, 'city')}
                required={true}
                errorText={t("Richieste lettere e spazi")}
                disable={!isEditing}
              />
            </Grid>
            <Grid item xs={4} sm={3}>
              <TextFieldCustom
                label={t("CAP")}
                regex={/^[0-9]+$/}
                value={formData.cap === 0 || formData.cap === undefined ? '' : formData.cap.toString()}
                onChange={(value) => handleChange(value, 'cap')}
                required={true}
                errorText={t("Richiesti solo Numeri")}
                disable={!isEditing}
              />
            </Grid>
            <Grid item xs={8} sm={4}>
              <TextFieldCustom
                label={t("Provincia")}
                regex={new RegExp("^[\\p{L} \\-']{2,50}$", "u")}
                value={formData.provincia || '' }
                onChange={(value) => handleChange(value, 'provincia')}
                required={true}
                errorText={t("Richieste solo Lettere")}
                disable={!isEditing}
                props={{
                  sx: {
                    '& .MuiOutlinedInput-notchedOutline': {
                        // borderColor: GREEN_LOGO,
                        color: GREEN_MAIN,
                    },
                    '& .MuiInputLabel-root': {
                        top: -4,
                        fontSize: '1rem',
                        //color: GREEN_MAIN,
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                        color: GREEN_MAIN,
                    },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: GREEN_LOGO,
                        color: GREEN_MAIN,
                    },
                    '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'grey !important',
                    },
                    '& .MuiOutlinedInput-input': {
                        padding: '12px 12px',
                        lineHeight: 1.4,
                        color: "grey",
                        fontSize: '1rem',
                        textTransform: 'uppercase'
                    },
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <SearchNations 
                label={t("Stato")}
                nazione={formData.nation || ''} 
                setNazione={(value) => handleChange(value, 'nation')} 
                disable={!isEditing}
              />
            </Grid>

            <Grid item xs={12} sm={7} md={6}>
              <TextFieldCustom
                label={t("E-mail")}
                value={formData.email || '' }
                errorText={t("")}
                props={{
                  sx:{
                    '& .MuiOutlinedInput-input': {
                      textTransform: 'lowercase'
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={5} md={6}>
              <TextFieldCustom
                label={t("Telefono")}
                regex={/^\+?[0-9\s\-()]{7,20}$/}
                value={formData.telephone?.toString() || '' }
                onChange={(value) => handleChange(value, 'telephone')}
                required={false}
                errorText={t("Richiesti solo Numeri")}
                disable={!isEditing}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextFieldCustom
                label={t("Professione")}
                regex={/^[A-Za-zÀ-ÖØ-öø-ÿ][A-Za-zÀ-ÖØ-öø-ÿ'’\- ]*$/}
                value={formData.professione || ''}
                onChange={(value) => handleChange(value, 'professione')}
                required={true}
                errorText={t("Richieste lettere e spazi")}
                disable={!isEditing}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextFieldCustom
                label={t("Associazione di appartenenza")}
                value={formData.association || '' }
                errorText={t("")}
              />
            </Grid>
            
            {/*<Grid item xs={12}>
              <Typography sx={{
                padding:'16px',
                color: GREEN_MAIN,
                fontSize:'0.9rem'
              }}>
                {t(getAssociationStatusLabel(formData.approvedFromAssociation)) }
              </Typography>
            </Grid>*/}
          </Grid>
        </Box>
      </form>
      
      
      {!isForbidden ? (
        !isEditing ? (
          <Grid container justifyContent="center" alignItems="center" spacing={3}>
            <Grid item xs={6} sm={4} md={2}>
              <Button variant="contained" color="info" onClick={handleEdit} sx={{
                margin:"10px 0 0 0",
                height: '35px',
                color:WHITE,
                backgroundColor: GREEN_LOGO,
                "&:hover": { backgroundColor: "darkgreen" },
                border: "1px solid " + GREEN_LOGO,
              }}>
                {t("Modifica")}
              </Button>
            </Grid>
          </Grid>
        ) : (
          <Grid container justifyContent="center" alignItems="center" spacing={3}>
            <Grid item xs={6} sm={4} md={2}>
              <Button variant="outlined" onClick={handleCancel} sx={{
                margin:"10px 0 0 0",
                height: '35px',
                "&:hover": { borderColor: "darkgreen" },
              }}>
                {t("Annulla")}
              </Button>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Button variant="contained" color="info" onClick={handleOpenModal} sx={{
                margin:"10px 0 0 0",
                height: '35px',
                color:WHITE,
                backgroundColor: GREEN_LOGO,
                "&:hover": { backgroundColor: "darkgreen" },
                border: "1px solid " + GREEN_LOGO,
              }}>
                {t("Salva")}
              </Button>
            </Grid>
          </Grid>
        )
      ) : null}


      {mainError && <>
          <Typography
            variant="body2" // Tipo di testo
            color="error"   // Colore rosso per il messaggio di errore
            sx={{
              maxHeight: '6.5em',        // Limite massimo di altezza per circa 5 righe (1.3em per linea)
              overflow: 'hidden',        // Nasconde l'overflow
              textOverflow: 'ellipsis',  // Aggiunge i puntini di sospensione se il testo è troppo lungo
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 5,        // Limita il numero massimo di righe a 5
              lineHeight: '1.3em',       // Altezza della riga per calcolare il numero di righe
              wordWrap: 'break-word',    // Va a capo automaticamente
              paddingTop:'20px'
            }}
          >
          {mainErrorMex}
        </Typography>
      </>
      }

      <AlertSaveModal
        open={modalOpen}
        onClose={handleCloseModal}
        onSave={handleSave}
      />

      {/* Popup che appare dopo il salvataggio */}
      <Snackbar open={showPopup} anchorOrigin={{vertical: 'bottom', horizontal: "right" }}>
        <Alert severity="success"
          sx={{ 
            border: '2px solid green',
            marginBottom:'30px'
          }}
          onClose={() => setShowPopup(false)}>
          {t("Salvataggio dati avvenuto con successo")}
        </Alert>
      </Snackbar>
    </Box>
  );
};


export default PrivateUser;
